import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { ArrowLeft, PaperPlaneRight, FileArrowUp, Check, X, ChatText, MagicWand } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

const STATUS_FLOW = ["draft", "sent", "acknowledged", "response_received", "under_review", "accepted", "rejected"];

export default function ScmInquiry() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inq, setInq] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [email, setEmail] = useState(null);
  const [msg, setMsg] = useState("");
  const [showResponse, setShowResponse] = useState(false);
  const [respForm, setRespForm] = useState({ lead_time_weeks: "", price: "", notes: "" });
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [q, v] = await Promise.all([api.get(`/scm/inquiries/${id}`), api.get("/vendors")]);
      setInq(q.data); setVendors(v.data);
    } catch (e) { toast.error(fmtErr(e)); nav("/scm"); }
  };
  useEffect(() => { load(); }, [id]);

  const setVendor = async (vendor_id) => {
    try { await api.patch(`/scm/inquiries/${id}`, { vendor_id }); toast.success("Vendor assigned"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const compose = async () => {
    try { const { data } = await api.post(`/scm/inquiries/${id}/compose`); setEmail(data); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const send = async () => {
    try {
      const { data } = await api.post(`/scm/inquiries/${id}/send`, { text: `Subject: ${email.subject}\n\n${email.body}`, direction: "out" });
      toast.success(`Sent (tracked) · expected response by ${data.expected_date}`);
      setEmail(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addMessage = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/scm/inquiries/${id}/messages`, { text: msg, direction: "note" }); setMsg(""); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const logResponse = async () => {
    try {
      const fd = new FormData();
      const f = fileRef.current?.files?.[0];
      if (f) fd.append("file", f);
      fd.append("lead_time_weeks", respForm.lead_time_weeks);
      fd.append("price", respForm.price);
      fd.append("notes", respForm.notes);
      const { data } = await api.post(`/scm/inquiries/${id}/response`, fd);
      toast.success("Response logged");
      if (data.response_data?.extracted) toast.success("AI extracted datasheet values — review below");
      setShowResponse(false); setRespForm({ lead_time_weeks: "", price: "", notes: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const accept = async () => {
    try {
      await api.post(`/scm/inquiries/${id}/accept`);
      toast.success("Accepted — vendor data fed into design engine");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const reject = async () => {
    try {
      await api.post(`/scm/inquiries/${id}/reject`, { reason: rejectReason });
      toast.success("Rejected — select an alternative vendor");
      setShowReject(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!inq) return <div className="p-6 font-mono text-xs text-[var(--c-muted)] tracking-widest" data-testid="inquiry-loading">LOADING INQUIRY…</div>;

  const statusIdx = STATUS_FLOW.indexOf(inq.status);
  const catVendors = vendors.filter((v) => v.active && (!inq.category || v.categories.includes(inq.category)));
  const suggested = vendors.filter((v) => v.active && inq.category && v.categories.includes(inq.category));

  return (
    <div className="p-6" data-testid="inquiry-detail-page">
      <button onClick={() => nav("/scm")} data-testid="back-to-scm"
        className="flex items-center gap-1.5 text-xs text-[var(--c-muted)] hover:text-[var(--c-text)] transition-colors duration-150 mb-4">
        <ArrowLeft size={13} /> SCM & Vendors
      </button>

      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-xs tracking-wider text-[var(--c-muted)]" data-testid="inquiry-id">{inq.inquiry_id}</span>
          <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
            inq.status === "rejected" ? "text-[var(--c-red)] border-[var(--c-red)]/40 bg-[var(--c-red)]/10" :
            inq.status === "accepted" ? "text-[var(--c-green)] border-[var(--c-green)]/40 bg-[var(--c-green)]/10" :
            "text-[var(--c-accent)] border-[var(--c-accent)]/40 bg-[var(--c-accent)]/10"}`} data-testid="inquiry-status">
            {inq.status.replace(/_/g, " ")}
          </span>
          {inq.urgency === "high" && <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-red)]">High urgency</span>}
        </div>
        <h1 className="text-xl tracking-tight font-medium text-[var(--c-text)] mt-2" data-testid="inquiry-component">{inq.component}</h1>
        <div className="text-xs text-[var(--c-muted)] mt-1">{inq.project_code} · {inq.product_summary}</div>
        <div className="flex items-center gap-0 mt-4 overflow-x-auto" data-testid="inquiry-pipeline">
          {STATUS_FLOW.filter((s) => s !== "rejected").map((s, i) => (
            <div key={s} className="flex items-center shrink-0">
              <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border whitespace-nowrap ${
                i < statusIdx ? "border-[var(--c-green)] text-[var(--c-green)]" :
                i === statusIdx ? "border-[var(--c-accent)] text-[var(--c-text)] bg-[var(--c-accent)]/15" :
                "border-[var(--c-border)] text-[var(--c-muted)]"}`} data-testid={`pipeline-${s}`}>
                {s.replace(/_/g, " ")}
              </span>
              {i < STATUS_FLOW.length - 2 && <div className={`w-4 h-px ${i < statusIdx ? "bg-[var(--c-green)]" : "bg-[var(--c-border)]"}`} />}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="space-y-4">
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="inquiry-details">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Component & Requirements</div>
            <div className="space-y-2 text-xs">
              {[["Category", inq.category || "—"], ["Quantity", inq.quantity || "—"],
                ["Required response", inq.required_date || "—"],
                ["Expected delivery", inq.expected_date || "—"],
                ["Project delivery", inq.project_delivery_date || "—"],
                ["Days remaining", inq.days_remaining != null ? `${inq.days_remaining}d` : "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 border-b border-[var(--c-border)]/50 last:border-0">
                  <span className="text-[var(--c-muted)]">{k}</span>
                  <span className={`font-mono ${k === "Days remaining" && inq.overdue ? "text-[var(--c-red)]" : "text-[var(--c-text)]"}`}>{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">Specs needed from vendor</div>
              <div className="flex flex-wrap gap-1.5" data-testid="inquiry-specs">
                {(inq.specs_needed || []).map((s) => (
                  <span key={s} className="text-[10px] px-2 py-0.5 rounded-sm bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)]">{s}</span>
                ))}
                {(inq.specs_needed || []).length === 0 && <span className="text-xs text-[var(--c-muted)]">—</span>}
              </div>
            </div>
            {inq.drawings_requested && <div className="text-[11px] text-[var(--c-amber)] mt-3">Drawings/datasheets requested with offer</div>}
            {inq.notes && <div className="text-xs text-[var(--c-muted)] mt-3 border-t border-[var(--c-border)]/50 pt-2">{inq.notes}</div>}
          </div>

          {inq.response_data && (
            <div className="bg-[var(--c-surface)] border border-[var(--c-green)]/40 rounded-sm p-5" data-testid="response-data">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-green)] mb-3">Vendor Response</div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-[var(--c-muted)]">Lead time</span><span className="font-mono text-[var(--c-text)]">{inq.response_data.lead_time_weeks || "—"} weeks</span></div>
                <div className="flex justify-between"><span className="text-[var(--c-muted)]">Price</span><span className="font-mono text-[var(--c-text)]">{inq.response_data.price || "—"}</span></div>
                <div className="flex justify-between"><span className="text-[var(--c-muted)]">Files</span><span className="font-mono text-[var(--c-text)]">{(inq.response_data.files || []).map((f) => f.filename).join(", ") || "—"}</span></div>
              </div>
              {inq.response_data.extracted && (
                <div className="mt-3 border-t border-[var(--c-border)]/50 pt-3" data-testid="extracted-review">
                  <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">AI-extracted datasheet values</div>
                  {Object.entries(inq.response_data.extracted).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs py-1">
                      <span className="text-[var(--c-muted)]">{k}</span>
                      <span className="font-mono text-[var(--c-accent)]">{v === null ? "—" : String(v)}</span>
                    </div>
                  ))}
                </div>
              )}
              {inq.response_data.extracted_note && <div className="text-[11px] text-[var(--c-amber)] mt-2">{inq.response_data.extracted_note}</div>}
              <div className="flex gap-2 mt-4">
                <Button data-testid="accept-btn" onClick={accept} disabled={inq.status === "accepted"}
                  className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-8 text-xs">
                  <Check size={13} className="mr-1" weight="bold" /> Accept — feed to design
                </Button>
                <Button data-testid="reject-btn" onClick={() => setShowReject(true)} variant="outline"
                  className="border-[var(--c-red)]/40 text-[var(--c-red)] hover:bg-[var(--c-red)]/10 rounded-sm h-8 text-xs">
                  <X size={13} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="vendor-selection">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Vendor</div>
            {suggested.length > 0 && !inq.vendor_id && (
              <div className="text-[11px] text-[var(--c-accent)] mb-2">Suggested for "{inq.category}": {suggested.map((v) => v.name).join(", ")}</div>
            )}
            <Select value={inq.vendor_id || ""} onValueChange={setVendor}>
              <SelectTrigger data-testid="inquiry-vendor" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]">
                <SelectValue placeholder="Select vendor…" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {catVendors.map((v) => (
                  <SelectItem key={v.id} value={v.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
                    {v.name} {v.avg_lead_weeks != null && <span className="text-[var(--c-muted)]">· {v.avg_lead_weeks}w</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 mt-3">
              <Button data-testid="compose-btn" onClick={compose} disabled={!inq.vendor_id} variant="outline"
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
                <MagicWand size={13} className="mr-1" /> Compose Email
              </Button>
              <Button data-testid="log-response-btn" onClick={() => setShowResponse(true)}
                className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
                <FileArrowUp size={13} className="mr-1" /> Log Response
              </Button>
            </div>
          </div>

          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="comm-thread">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">Communication Thread</div>
            <div className="space-y-2.5 max-h-72 overflow-y-auto">
              {(inq.communications || []).map((c, i) => (
                <div key={i} className={`text-xs rounded-sm border px-3 py-2.5 ${
                  c.direction === "in" ? "border-[var(--c-green)]/30 bg-[var(--c-green)]/5" :
                  c.direction === "out" ? "border-[var(--c-accent)]/30 bg-[var(--c-accent)]/5" :
                  "border-[var(--c-border)] bg-[var(--c-bg)]"}`} data-testid={`comm-${i}`}>
                  <div className="flex justify-between text-[10px] font-mono text-[var(--c-muted)] mb-1">
                    <span className="uppercase tracking-wider">{c.direction} · {c.by}</span>
                    <span>{String(c.ts || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-[var(--c-text)] whitespace-pre-wrap leading-relaxed">{c.text}</div>
                  {(c.attachments || []).length > 0 && <div className="text-[10px] text-[var(--c-accent)] mt-1 font-mono">{c.attachments.join(", ")}</div>}
                </div>
              ))}
              {(inq.communications || []).length === 0 && <div className="text-xs text-[var(--c-muted)] py-3 text-center">No messages yet.</div>}
            </div>
            <div className="flex gap-2 mt-3">
              <Input data-testid="comm-input" value={msg} onChange={(e) => setMsg(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addMessage()}
                placeholder="Add note to thread…" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" />
              <Button data-testid="comm-add-btn" onClick={addMessage} size="sm"
                className="bg-[var(--c-border)] hover:bg-[var(--c-border)]/70 text-[var(--c-text)] h-8 rounded-sm"><ChatText size={13} /></Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!email} onOpenChange={() => setEmail(null)}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">RFQ Email</DialogTitle>
            <DialogDescription className="sr-only">Composed vendor inquiry email</DialogDescription>
          </DialogHeader>
          {email && (
            <div className="space-y-3">
              <Input data-testid="email-subject" value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] text-xs font-mono" />
              <textarea data-testid="email-body" value={email.body} onChange={(e) => setEmail({ ...email, body: e.target.value })}
                rows={14} className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2 text-xs text-[var(--c-text)] font-mono focus:outline-none focus:ring-2 focus:ring-[var(--c-accent)]" />
              <Button data-testid="email-send-btn" onClick={send} className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
                <PaperPlaneRight size={14} className="mr-1.5" weight="fill" /> Send (tracked)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showResponse} onOpenChange={setShowResponse}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="response-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">Log Vendor Response</DialogTitle>
            <DialogDescription className="sr-only">Upload vendor datasheet and response details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Datasheet / offer file (PDF, DOCX, XLSX — AI-parsed)</Label>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx" data-testid="response-file"
                className="block w-full text-xs text-[var(--c-muted)] file:bg-[var(--c-border)] file:border-0 file:rounded-sm file:px-3 file:py-2 file:text-xs file:text-[var(--c-text)] file:mr-3" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Lead time (weeks)</Label>
                <Input data-testid="response-lead" type="number" value={respForm.lead_time_weeks} onChange={(e) => setRespForm({ ...respForm, lead_time_weeks: e.target.value })}
                  className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[var(--c-muted)] text-xs">Price</Label>
                <Input data-testid="response-price" value={respForm.price} onChange={(e) => setRespForm({ ...respForm, price: e.target.value })}
                  placeholder="e.g., 18400 USD" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] font-mono" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Notes</Label>
              <Input data-testid="response-notes" value={respForm.notes} onChange={(e) => setRespForm({ ...respForm, notes: e.target.value })}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
            </div>
            <Button data-testid="response-submit" onClick={logResponse} className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">Log Response</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--c-text)]">Reject Response</DialogTitle>
            <DialogDescription className="sr-only">Reason required for rejection</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason (required) — e.g., dims incompatible with tank design" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
            <Button data-testid="reject-submit" onClick={reject} disabled={!rejectReason.trim()}
              className="w-full bg-[var(--c-red)] hover:bg-[var(--c-red)]/85 text-[var(--c-text)] rounded-sm">Reject & Try Alternative Vendor</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
